import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Delete, DragIndicatorRounded, ExpandLess, ExpandMore } from '@mui/icons-material'
import { IconButton, MenuItem, Paper, Stack, TextField, Tooltip } from '@mui/material'
import { useState } from 'react'
import DateFieldConfig from './DateFieldConfig'
import NumberFieldConfig from './NumberFieldConfig'
import RequiredButton from './RequiredButton'
import { EditorField, FieldType, SchemaEditorAction, SchemaEditorState } from './schemaEditorReducer'
import StringFieldConfig from './StringFieldConfig'
import ObjectFieldConfig from './ObjectFieldConfig'
import ListFieldConfig from './ListFieldConfig'
import BoolFieldConfig from './BoolFieldConfig'

interface P {
    state: SchemaEditorState
    field: EditorField
    hasKeyError?: boolean
    dispatch: React.Dispatch<SchemaEditorAction>
    onDelete: (id: string) => void
    onUpdateField: (fieldId: string, updates: Partial<EditorField>) => void

}

const SortableFieldItem = ({ field, state, hasKeyError, onDelete, onUpdateField, dispatch }: P) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: field._id,
        disabled: field.isDefault
    })
    const [expanded, setExpanded] = useState(false)

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    }

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            sx={{ p: 2 }}>
            <Stack spacing={2}>

                <Stack
                    spacing={1}
                    direction='row'
                    alignItems='center'>

                    <Stack
                        {...attributes}
                        {...listeners}
                        sx={{ cursor: 'grab' }}>
                        <DragIndicatorRounded color={field.isDefault ? 'disabled' : 'inherit'} />
                    </Stack>

                    <Stack
                        spacing={2}
                        direction='row'
                        sx={{ flex: 1
                        }}>

                        <Tooltip arrow title='Mandatory Field'>
                            <Stack>
                                <RequiredButton
                                    isDefault={field.isDefault}
                                    onToggle={() => onUpdateField(field._id, {
                                        validation: {
                                            ...field.validation,
                                            isRequired: !field.validation?.isRequired
                                        } })}
                                    isRequired={field.validation?.isRequired ?? false}
                                />
                            </Stack>
                        </Tooltip>

                        <TextField
                            disabled={field.isDefault}
                            size='small'
                            label='Label'
                            value={field.label}
                            onChange={e => onUpdateField(field._id, { label: e.target.value })}
                        />
                        <TextField
                            disabled={field.isDefault}
                            size='small'
                            label='Key'
                            value={field.key}
                            error={hasKeyError}
                            helperText={hasKeyError ? 'Key must be unique' : ''}
                            onChange={e => onUpdateField(field._id, { key: e.target.value })}
                        />
                        <TextField
                            disabled={field.isDefault}
                            select
                            size='small'
                            label='Type'
                            value={field.type}
                            onChange={e => onUpdateField(field._id, { type: e.target.value as FieldType })}>{(field.isDefault
                                ? ['string',
                                        'number',
                                        'date',
                                        'long-form',
                                        'bool',
                                        'system-config',
                                        'list',
                                        'object',
                                        'string-options']
                                : ['string',
                                        'number',
                                        'date',
                                        'bool',
                                        'list',
                                        'object']).map(type => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <Stack>
                        <Tooltip arrow title={field.isDefault ? 'Default fields can\'t be deleted' : 'Delete'}>
                            <span>

                                <IconButton
                                    color='error'
                                    disabled={field.isDefault}
                                    onClick={e => {
                                        e.stopPropagation()
                                        onDelete(field._id)
                                    }}>
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Stack>
                        <IconButton
                            size='small'
                            onClick={e => {
                                e.stopPropagation()
                                setExpanded(prev => !prev)
                            }}>
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Stack>

                </Stack>
                {expanded && (
                    <>
                        {field.type === 'string' && (<StringFieldConfig field={field} onUpdateField={onUpdateField} />)}
                        {field.type === 'number' && (<NumberFieldConfig field={field} onUpdateField={onUpdateField} />)}
                        {field.type === 'date' && (<DateFieldConfig field={field} onUpdateField={onUpdateField} />)}
                        {field.type === 'object' && <ObjectFieldConfig state={state} dispatch={dispatch} field={field} onUpdateField={onUpdateField} />}
                        {field.type === 'list' && <ListFieldConfig state={state} dispatch={dispatch} field={field} onUpdateField={onUpdateField} />}
                        {field.type === 'bool' && <BoolFieldConfig field={field} onUpdateField={onUpdateField} />}
                        {/* {field.type === 'bool' && <} */}
                    </>
                )}
            </Stack>
        </Paper>
    )
}

export default SortableFieldItem
